import { Component, OnInit, OnDestroy, Input, viewChild, ElementRef, Output, EventEmitter, ViewChild, ViewContainerRef, TemplateRef, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import { fromEvent, switchMap, Subscription, forkJoin, takeUntil, Subject, Observable, of, catchError, timer, BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { ProductsService } from '@app/sales/products/products.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { AlertService } from '@app/shared/alert.service';
import { LoginService } from '@app/common/login.service';
import { FseService } from '@app/fse/fse.service';
import { ProductsComponent } from '@app/sales/products/products.component';
import { Product } from '../_models/product';
import { ScanResult } from '../_models/scan';


interface ProductSearchResult {
  id: string;
  name: string;
  tpl: string;
  is_sold: string;
  ptx?: string;
  pti?: string;
}

@Component({
  selector: 'app-search-product',
  standalone: true,
  templateUrl: './search-product.component.html',
  styleUrls: ['./search-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, OverlayModule, PortalModule]
})
export class SearchProductComponent implements OnInit, OnDestroy {
  @Input() width: string = '100%';  // Largeur du wrapper (ex: '100%', '250px', 'auto')
  @Input() placeholder: string = 'Recherche produit';
  @Input() clearButtonVisible: boolean = true;
  @Input() smallHeight: boolean = false;
  @Input() disabled: boolean = false;
  @Output() blurEmitter = new EventEmitter<string>();
  @Output() selectProductEmitter = new EventEmitter<any>();

  @ViewChild('trigger') triggerElement!: ElementRef<HTMLElement>;
  @ViewChild('panel') panelTemplate!: TemplateRef<any>;
  @ViewChild('errorPanel') errorPanelTemplate!: TemplateRef<any>;
  
  @Input() set is_ht(value: boolean) { this._is_ht$.next(value); }
  get is_ht(): boolean { return this._is_ht$.getValue(); }
  private readonly _is_ht$ = new BehaviorSubject<boolean>(false);

  public searchProductInput = viewChild<ElementRef>('searchproductInput');
  public searchnameProduct: string = '';
  public loadingProduct: boolean = false;
  public notFindProduct: boolean = false;
  public productsApiResponse: ProductSearchResult[] = [];
  public isFocused: boolean = false;
  public isOpen: boolean = false;
  public selectedIndex = -1;


  private overlayRef: OverlayRef | null = null;
  private errorOverlayRef: OverlayRef | null = null;
  private portal: TemplatePortal<any> | null = null;
  private errorPortal: TemplatePortal<any> | null = null;

  private readonly searchTimer: number = 1500;
  private subscriptions: Array<Subscription> = [];
  private destroySearch$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    public loginService: LoginService,
    private productsService: ProductsService,
    private spinner: SpinnerService,
    private alertService: AlertService,
    public dialog: MatDialog,
    private fseService: FseService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    // // FOR DEV
    // const devSearchValues = ['6393', '1234567890123456', '109740', '10267'];
    // for (let i = 0; i < 1; i++) {
    //   setTimeout(() => {
    //     this.searchnameProduct = devSearchValues[i % 4];
    //     this.blurEmitter.emit(this.searchnameProduct);
    //   }, 950 + i * 70);
    // }

    const inputEl = this.searchProductInput()?.nativeElement;
    if (!inputEl) return;

    this.subscriptions.push(
      fromEvent(inputEl, 'blur').subscribe(() => {
        this.isFocused = false;
        this.loadingProduct = false;
        this.productsApiResponse = [];
        this.close();
        this.closeError();
        this.destroySearch$.next();
        this.blurEmitter.emit(this.searchnameProduct);
      })
    );

    this.subscriptions.push(
      fromEvent<KeyboardEvent>(inputEl, 'keyup').pipe(
        filter((e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            this.loadingProduct = false;
            this.productsApiResponse = [];
            this.close();
            this.closeError();
            inputEl.blur();
            this.destroySearch$.next();
            return false;
          }
          return true;
        }),
        filter((e: KeyboardEvent) => !['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(e.key)),
        map(() => {
          this.notFindProduct = false;
          this.productsApiResponse = [];
          this.close();
          this.closeError();
          return (inputEl.value as string).trim();
        }),
        distinctUntilChanged(),
        filter((text: string) => {
          if (text.length < 3) {
            this.loadingProduct = false;
            this.close();
            this.closeError();
            this.destroySearch$.next();
            return false;
          }
          this.loadingProduct = true;
          return true;
        }),
        switchMap((text: string) =>
          timer(this.searchTimer).pipe(
            takeUntil(this.destroySearch$),
            map(() => text)
          )
        ),
        switchMap((text: string) => {
          this.loadingProduct = true;
          return this.hotSearchProducts(text).pipe(
            takeUntil(this.destroySearch$),
            catchError(err => {
              this.loadingProduct = false;
              console.error('error', err);
              return of([]);
            })
          );
        })
      ).subscribe(res => {
        this.loadingProduct = false;
        this.productsApiResponse = res;
        this.selectedIndex = -1;
        if (res.length === 0) {
          this.notFindProduct = true;
          this.openError();
        } else {
          this.open();
        }
        inputEl.focus();
      })
    );
  }

  // --- Overlay ---

  private getInputWidth(): number {
    return this.triggerElement?.nativeElement.getBoundingClientRect().width ?? 0;
  }

  private createOverlay(): void {
    if (this.overlayRef) return;
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerElement)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
  }

  private createErrorOverlay(): void {
    if (this.errorOverlayRef) return;
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerElement)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
    this.errorOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
  }

  open(): void {
    if (this.isOpen || this.productsApiResponse.length === 0) return;
    this.closeError();
    this.createOverlay();
    this.overlayRef!.updateSize({ width: this.getInputWidth() });
    this.isOpen = true;
    if (!this.overlayRef!.hasAttached()) {
      this.portal = new TemplatePortal(this.panelTemplate, this.viewContainerRef);
      this.overlayRef!.attach(this.portal);
    }
    this.overlayRef!.outsidePointerEvents().pipe(takeUntil(this.destroy$)).subscribe((event: MouseEvent) => {
      if (!this.triggerElement.nativeElement.contains(event.target as HTMLElement)) {
        this.close();
      }
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.selectedIndex = -1;
    this.overlayRef?.detach();
  }

  openError(): void {
    if (this.errorOverlayRef?.hasAttached()) return;
    this.close();
    this.createErrorOverlay();
    this.errorOverlayRef!.updateSize({ width: this.getInputWidth() });
    if (!this.errorOverlayRef!.hasAttached()) {
      this.errorPortal = new TemplatePortal(this.errorPanelTemplate, this.viewContainerRef);
      this.errorOverlayRef!.attach(this.errorPortal);
    }
    this.errorOverlayRef!.outsidePointerEvents().pipe(takeUntil(this.destroy$)).subscribe((event: MouseEvent) => {
      if (!this.triggerElement.nativeElement.contains(event.target as HTMLElement)) {
        this.closeError();
      }
    });
  }

  closeError(): void {
    this.notFindProduct = false;
    this.errorOverlayRef?.detach();
  }

  // --- Actions ---

  onOptionClick(option: ProductSearchResult): void {
    if (!option?.id) return;
    this.productsApiResponse = [];
    this.notFindProduct = false;
    this.searchnameProduct = '';
    this.close();
    this.closeError();
    const p = new Product();
    p.id = Number(option.id);
    p.name = option.name;
    p.template = Number(option.tpl);
    p.is_sold = option.is_sold;
    // p.price_taxi = parseFloat(option.pti ?? '') || 0;
    // p.price_taxx = parseFloat(option.ptx ?? '') || 0;
    this.selectProductEmitter.emit(p);
  }

  clearProductSearch() {
    this.notFindProduct = false;
    this.searchnameProduct = '';
    this.productsApiResponse = [];
    this.loadingProduct = false;
    this.close();
    this.closeError();
    this.destroySearch$.next();
    this.selectProductEmitter.emit(undefined);
    this.searchProductInput()?.nativeElement.select();
  }

  focus() {
    this.clearProductSearch();
    const el = this.searchProductInput()?.nativeElement;
    console.log('Salut from : focus inside search product');
    if (el) {
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        const gap = 20;
        const overflow = rect.bottom + gap - window.innerHeight;
        if (overflow > 0) { window.scrollBy({ top: overflow, behavior: 'smooth' }); }
      }); 75
    }
  }

  selectAllText() {
    const el = this.searchProductInput()?.nativeElement;
    if (el) { queueMicrotask(() => el.select()); }
  }

  onInputFocus(event: FocusEvent): void {
    this.isFocused = true;
    (event.target as HTMLInputElement).select();
    if (this.productsApiResponse.length > 0) {
      this.open();
    }
  }

  hotSearchProducts(text: string): Observable<ProductSearchResult[]> {
    console.log(' salut from searchGetCallName');
    text = text.trim();

    if (text === '') { return of([]); }

    let q = text;
    const split = text.split(' ');
    if (split.length > 1) {
      q = split[0];
      for (let i = 1; i < split.length; i++) {
        q += ' AND ' + split[i];
      }
    }

    const $search_i = this.productsService.search_i('*' + q + '*');
    // Sans espace, le texte tapé peut être un code (interne, EAN, GS1…) : /search/scan le décode et rend le conditionnement.
    const $search_bc = !text.includes(' ')
      ? this.productsService.search_scan(text).pipe(map((res: ScanResult) => res.ppv), catchError(() => of(null)))
      : of(null);

    return forkJoin({ i: $search_i, bc: $search_bc }).pipe(
      map(({ i, bc }: any) => {
        const results: ProductSearchResult[] = i;
        if (bc && bc.product && bc.lnk_product?.name && bc.lnk_product?.template) {
          if (!results.some(item => item.id === String(bc.product))) {
            results.push({
              id: String(bc.product),
              name: bc.lnk_product.name,
              tpl: String(bc.lnk_product.template),
              is_sold: bc.lnk_product.is_sold ? '1' : '0',
              ptx: bc.lnk_product.price_taxx?.toString(),
              pti: bc.lnk_product.price_taxi?.toString(),
            });
          }
        }
        return results;
      })
    );
  }

  formatPrice(price?: string): string {
    const n = parseFloat(price ?? '');
    return isNaN(n) ? '' : n.toFixed(2);
  }

  openProductsDialog(searchname: string = '') {
    this.close();
    this.closeError();
    const dialogRef = this.dialog.open(ProductsComponent, {
      width: '80%',
      height: '80%',
      data: { mode: 'DIALOG', searchname: searchname },
    });
    dialogRef.afterClosed().subscribe(ret => {
      if (ret) {
        this.selectProductEmitter.emit(ret);
      }
      this.clearProductSearch();
    });
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' && this.productsApiResponse.length > 0) {
        event.preventDefault();
        this.open();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.productsApiResponse.length - 1);
        this.scrollToSelected();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.scrollToSelected();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0) this.onOptionClick(this.productsApiResponse[this.selectedIndex]);
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  private scrollToSelected(): void {
    const items = this.overlayRef?.overlayElement?.querySelectorAll('.product-option');
    items?.[this.selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }

  onSearch(): void {
    console.log(' salut from onSearch inside search product component');
  }

  search_productpackaging(_index: string): void {
    console.log(' salut from search_productpackaging');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroySearch$.next();
    this.destroySearch$.complete();
    this.overlayRef?.dispose();
    this.errorOverlayRef?.dispose();
    this.subscriptions.forEach(sub => sub?.unsubscribe());
  }
}
